import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import { Flex } from '@adsk/alloy-react-box';
import { LinkButton } from '@adsk/alloy-react-button';
import { displayDate, VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import FormField from '@adsk/alloy-react-form-field';
import { XIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import Panel from '@adsk/alloy-react-panel';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';
import { VirtualizedSelectBox } from '@adsk/alloy-select-box';

import { SHEET_UNTAGGED } from './helpers/consts';
import { useSheetFilter } from './providers/SheetFilterProvider';
import FiltersHeader from '../../FiltersHeader';

const SheetFilter = ({
  filters,
  hasFilters,
  floatingPanel,
  clearFilters,
  setFilters,
  onClose,
  open,
}) => {
  const { T, locale } = useLocale(({ locale }) =>
    require(`./i18n/${locale}.json`),
  );
  const {
    versionSets: versionSetsList,
    tags,
    versionSetsLoading,
    versionSetsError,
    tagsLoading,
    tagsError,
    retryTags,
    retryVersionSets,
  } = useSheetFilter();
  const loading = versionSetsLoading && tagsLoading;

  const versionSets = useMemo(
    () =>
      versionSetsList.map((set) => ({
        ...set,
        subtext: T.translate('VERSION_SET_DATE', {
          issuanceDate: set.issuanceDate
            ? displayDate(new Date(set.issuanceDate), {
                format: VALID_DATE_FORMATS.DEFAULT,
                locale,
              })
            : '',
        }),
      })),
    [versionSetsList, T, locale],
  );

  const getFilters = (filters) => {
    Object.keys(filters).forEach((key) => {
      const filter = filters[key];
      if (!filter || (Array.isArray(filter) && filter.length < 1)) {
        delete filters[key];
      }
    });
    return filters;
  };

  const footerRenderer = (type) => {
    const isLoading = type === 'tags' ? tagsLoading : versionSetsLoading;
    const isFailure = type === 'tags' ? tagsError : versionSetsError;
    let content = null;

    if (isLoading) content = T.translate('LOAD_MORE');
    if (isFailure)
      content = (
        <div>
          {T.translate('ERROR_LOADING_RESULTS')}
          <LinkButton
            style={{ marginLeft: 4 }}
            onClick={type === 'tags' ? retryTags : retryVersionSets}
          >
            {T.translate('TRY_AGAIN')}
          </LinkButton>
        </div>
      );

    return (
      content && (
        <Flex
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          padding="8px"
        >
          {content}
        </Flex>
      )
    );
  };

  const valueTagRenderer = (item) => {
    return (
      <Flex
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
        backgroundColor="charcoal100"
        padding="4px 8px"
        borderRadius="4px"
        margin="2px"
        style={{
          '&:hover': {
            background: theme.colors.charcoal200,
            cursor: 'default',
          },
        }}
      >
        <div style={{ maxWidth: 160 }}>
          <OverflowTooltip
            style={{
              maxWidth: 300,
              wordBreak: 'normal',
              wordWrap: 'break-word',
            }}
            autoTipOnEllipsis
          >
            {item.label}
          </OverflowTooltip>
        </div>
        <LinkButton
          style={{
            '&:focus': {
              'box-shadow': 'none',
            },
            marginLeft: 4,
            color: theme.colors.charcoal600,
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setFilters(
              getFilters({
                ...filters,
                tags: filters.tags.filter((tag) => tag !== item.value),
              }),
            );
          }}
        >
          <XIcon size={16} />
        </LinkButton>
      </Flex>
    );
  };

  return (
    <Panel
      open={open}
      floating={floatingPanel}
      renderHeader={() => (
        <FiltersHeader
          floatingPanel={floatingPanel}
          onClose={onClose}
          title={T.translate('FILTER_TITLE')}
          hasFilters={hasFilters}
          clearFilters={clearFilters}
        />
      )}
    >
      {loading ? (
        <Flex
          width="100%"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <ProgressRing size={ProgressRing.SIZES.MEDIUM} />
        </Flex>
      ) : (
        <Panel.Body style={{ overflow: 'auto' }}>
          <FormField
            label={T.translate('VERSION_SET')}
            labelVariant={FormField.LABEL_VARIANTS.TOP}
            style={{ marginBottom: 16 }}
          >
            <VirtualizedSelectBox
              searchable
              clearable
              valueKey="id"
              labelKey="name"
              options={versionSets}
              value={
                filters.setId
                  ? versionSets.find(({ id }) => id === filters.setId)
                  : null
              }
              isLoading={versionSets.length < 0 && versionSetsLoading}
              placeholder={T.translate('FILTER_BY_VERSION_SET')}
              onChange={(option) => {
                setFilters(
                  getFilters({
                    ...filters,
                    setId: option ? option.id : null,
                  }),
                );
              }}
              footerRenderer={() => footerRenderer('versionSets')}
            />
          </FormField>
          <FormField
            label={T.translate('TAG')}
            labelVariant={FormField.LABEL_VARIANTS.TOP}
          >
            <VirtualizedSelectBox
              style={{ maxHeight: 80, overflowY: 'auto' }}
              multiple
              searchable
              clearable
              showValueAsTags
              options={[
                {
                  value: SHEET_UNTAGGED,
                  label: T.translate('SHEET_UNTAGGED'),
                },
                ...tags.map((option) => ({
                  value: option,
                  label: option,
                })),
              ]}
              value={(filters.tags || [])
                .filter((tag) => [...tags, SHEET_UNTAGGED].includes(tag))
                .map((tag) => ({
                  value: tag,
                  label:
                    tag === SHEET_UNTAGGED
                      ? T.translate('SHEET_UNTAGGED')
                      : tag,
                }))}
              isLoading={tags.length < 0 && tagsLoading}
              placeholder={T.translate('FILTER_BY_TAG')}
              onChange={(items) => {
                setFilters(
                  getFilters({
                    ...filters,
                    tags: (items || []).map((option) => option.value),
                  }),
                );
              }}
              valueTagRenderer={(item) => valueTagRenderer(item)}
              footerRenderer={() => footerRenderer('tags')}
            />
          </FormField>
        </Panel.Body>
      )}
    </Panel>
  );
};

SheetFilter.propTypes = {
  filters: PropTypes.shape({
    setId: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }),
  floatingPanel: PropTypes.bool,
  hasFilters: PropTypes.bool,
  setFilters: PropTypes.func,
  clearFilters: PropTypes.func,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};

export default SheetFilter;
